import { useMemo, useState } from 'react'

import { ButtonText } from '@/components/common/Button'
import { FloatingTooltip } from '@/components/common/Floating'
import { IconWithLabel } from '@/components/icons/IconWithLabel'
import { type RecType } from '@/defs/rec'
import { clipboardActions } from '@/store/clipboard'
import { useReduxDispatch } from '@/store/hooks'
import { toastActions } from '@/store/toast'
import { fetchRecs } from '@/utils/apiUtil'
import { isNonEmptyString, parseError } from '@/utils/commonUtil'

type Props = {
  loading?: boolean
  onSuccess?: () => void
  recs: RecType[]
}

export const ClipboardButtonRecs = ({ loading: loadingCards, onSuccess, recs }: Props) => {
  const dispatch = useReduxDispatch()
  const [loadingRecs, setLoadingRecs] = useState(false)

  const loading = loadingRecs || loadingCards

  const inputs = useMemo(() => {
    const [commander, commander2] = recs
      .filter(({ role }) => role === 'commander')
      .map(({ name }) => name)
      .filter(isNonEmptyString)

    return {
      commander,
      commander2,
      decklist: recs
        .filter(({ role }) => role !== 'commander')
        .map(({ name }) => name)
        .filter(isNonEmptyString)
        .join('\n'),
      excludeLands: true,
    }
  }, [recs])

  const onClick = () => {
    setLoadingRecs(true)
    void (async () => {
      try {
        const { commander } = inputs
        if (!commander) return
        const { errors, inRecs } = await fetchRecs({ inputs: { ...inputs, commander } })

        if (errors) {
          dispatch(
            toastActions.add(
              <>
                <strong>Recs Error:</strong> {errors.join(', ')}
              </>,
            ),
          )
          return
        }
        if (!inRecs) {
          dispatch(toastActions.add(<>No recs were found from your current clipboard</>))
          return
        }
        const recsNew = inRecs.map((r) => ({ ...r, role: undefined }))
        dispatch(clipboardActions.addPending(recsNew))
        onSuccess?.()
      } catch (e) {
        dispatch(
          toastActions.add(
            <>
              <strong>Recs Error:</strong> {parseError(e, 'Recs error')}
            </>,
          ),
        )
      } finally {
        setLoadingRecs(false)
      }
    })()
  }

  const disabled = loading || recs.length < 1 || !inputs.commander

  const buttonElement = (
    <ButtonText disabled={disabled} onClick={onClick} size='sm'>
      <IconWithLabel iconBefore={loading ? 'spinner' : 'chartSimple'} iconBeforeSize='sm'>
        Recs
      </IconWithLabel>
    </ButtonText>
  )

  if (!loading && !inputs.commander) {
    return (
      <FloatingTooltip
        content={
          <p>
            To use the Recs button, first set a commander by clicking the crown icon on an eligible card in your
            Clipboard.
          </p>
        }
        styled
      >
        {buttonElement}
      </FloatingTooltip>
    )
  }

  return buttonElement
}
